import {
  isValidElement,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { AnimatePresence } from 'motion/react';
import { Dialog } from 'radix-ui';

import { DialogContext, DialogState } from '@components/primitives/Dialog';
import {
  ModalPartsContext,
  useModalAutoFocus,
} from '@components/primitives/focus';

import { DrawerDirection, DrawerDirectionContext } from './context';
import DrawerClose from './DrawerClose';
import DrawerContent from './DrawerContent';
import DrawerFooter from './DrawerFooter';
import DrawerHeader from './DrawerHeader';
import DrawerOverlay from './DrawerOverlay';
import DrawerTitle from './DrawerTitle';

interface DrawerProps extends PropsWithChildren {
  state: DialogState;
  trigger?: string | ReactNode;
  direction?: DrawerDirection;
  modal?: boolean;
  dismissible?: boolean;
  /** Pass a styled `Drawer.Overlay` to replace the default scrim, or `null` for none. */
  overlay?: ReactNode;
}

function Drawer({
  state,
  trigger,
  direction = 'right',
  modal,
  dismissible = true,
  overlay = <DrawerOverlay />,
  children,
}: DrawerProps) {
  const [triggerRef, autoFocusProps] = useModalAutoFocus(trigger);

  const onEscapeKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!dismissible) {
        event.preventDefault();
        return;
      }

      if (
        event.target &&
        event.target instanceof HTMLElement &&
        event.target.closest('[data-radix-popper-content-wrapper]')
      ) {
        event.preventDefault();
      }
    },
    [dismissible]
  );

  const onInteractOutside = useCallback(
    (event: Event) => {
      if (!dismissible) event.preventDefault();
    },
    [dismissible]
  );

  const parts = useMemo(
    () => ({ ...autoFocusProps, onEscapeKeyDown, onInteractOutside }),
    [autoFocusProps, onEscapeKeyDown, onInteractOutside]
  );

  return (
    <DialogContext.Provider value={state}>
      <DrawerDirectionContext.Provider value={direction}>
        <ModalPartsContext.Provider value={parts}>
          <Dialog.Root
            open={state.isOpen}
            onOpenChange={state.setIsOpen}
            modal={modal}
          >
            {isValidElement(trigger) && (
              <Dialog.Trigger ref={triggerRef} asChild>
                {trigger}
              </Dialog.Trigger>
            )}

            <AnimatePresence>
              {state.isOpen && (
                <Dialog.Portal forceMount>
                  {overlay}
                  {children}
                </Dialog.Portal>
              )}
            </AnimatePresence>
          </Dialog.Root>
        </ModalPartsContext.Provider>
      </DrawerDirectionContext.Provider>
    </DialogContext.Provider>
  );
}

const DrawerNamespace = Object.assign(Drawer, {
  Overlay: DrawerOverlay,
  Content: DrawerContent,
  Header: DrawerHeader,
  Title: DrawerTitle,
  Description: Dialog.Description,
  Close: DrawerClose,
  Footer: DrawerFooter,
});

export { DrawerNamespace as Drawer };

export type { DrawerDirection } from './context';
export { useDrawerScrollContainer } from './context';
export { useDialogContext as useDrawerContext } from '@components/primitives/Dialog/context';
export { default as useDrawer } from '@components/primitives/Dialog/useDialog';
